<template>
  <div v-show="editing" class="flex flex-col xl:flex-row justify-center text-center gap-4">
    <div class="flex flex-row justify-center gap-4">
      <Button label="New Borrower" icon="pi pi-user" outlined class="w-52" :class="buttonHeight" @click="addSourceClick('Borrower')"></Button>
      <div class="w-auto">
        <InputGroup :class="buttonHeight">
          <InputGroupAddon :class="(!borrowerSplitPossible ? 'opacity-60 ' : '') + 'border-primary-500! dark:border-primary-500!'">
            <ToggleSwitch
              v-model="projectSettings.enableEquityParticipation"
              :disabled="!borrowerSplitPossible"
              :class="condensed ? 'h-6!' : ''"
              @update:model-value="updateFundsDisbursed"
            ></ToggleSwitch>
          </InputGroupAddon>
          <Button
            label="Edit Borrower Splits"
            icon="pi pi-user"
            outlined
            :disabled="!projectSettings.enableEquityParticipation"
            class="w-52"
            @click="() => showParticipation('Borrower')"
          ></Button>
        </InputGroup>
      </div>
    </div>
    <div>
      <ToggleButton
        v-model="projectSettings.enableRevolvingCredit"
        on-label="Revolving Credit Enabled"
        off-label="Revolving Credit Disabled"
        :class="buttonHeight"
        @change="recalculate"
      ></ToggleButton>
    </div>

    <div class="flex flex-row justify-center mt-3 xl:mt-0 gap-4">
      <Button
        label="New Lender"
        icon="pi pi-user"
        outlined
        class="w-52 text-secondary-500 border-secondary-500 hover:bg-secondary-300/20 focus:ring-secondary-400/50! dark:focus:ring-secondary-300/50"
        :class="buttonHeight"
        @click="addSourceClick('Lender')"
      ></Button>
      <div class="w-auto">
        <InputGroup :class="buttonHeight">
          <InputGroupAddon :class="(!lenderSplitPossible ? 'opacity-60 ' : '') + 'border-secondary-500! dark:border-secondary-500!'">
            <ToggleSwitch
              v-model="projectSettings.enableLenderParticipation"
              :disabled="!lenderSplitPossible"
              :class="condensed ? 'h-6!' : ''"
              severity="info"
              :pt="{
                slider: ({ props }) => ({
                  class: [
                    {
                      'bg-secondary-500! dark:bg-secondary-400!': props.modelValue == props.trueValue,
                    },
                  ],
                }),
              }"
              @update:model-value="updateFundsDisbursed"
            ></ToggleSwitch>
          </InputGroupAddon>
          <Button
            label="Edit Lender Splits"
            icon="pi pi-user"
            outlined
            :disabled="!projectSettings.enableLenderParticipation"
            class="w-52 text-secondary-500 border-secondary-500 hover:bg-secondary-300/20 focus:ring-secondary-400/50 dark:focus:ring-secondary-300/50"
            @click="() => showParticipation('Lender')"
          ></Button>
        </InputGroup>
      </div>
    </div>
  </div>

  <div class="h-fit" style="width: 100%; flex: 1 1 auto">
    <ag-grid-vue
      theme="legacy"
      gridId="sourceOfFundsGrid"
      :rowData="projectEntities"
      :rowClassRules
      :columnDefs="colDefs"
      :defaultColDef="defaultColDefs"
      :autoSizeStrategy="{ type: 'fitGridWidth' }"
      :pinnedBottomRowData="sourceFooter"
      :singleClickEdit="true"
      :stopEditingWhenCellsLoseFocus="true"
      :suppressDragLeaveHidesColumns="true"
      :domLayout="condensed ? 'normal' : 'autoHeight'"
      :getRowHeight="getRowHeight"
      class="h-full pt-1 pb-2"
      :class="(darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz') + (condensed ? ' ag-font-small' : '')"
      @grid-size-changed="resize"
      @cell-editing-stopped="recalculate"
      @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
    ></ag-grid-vue>
  </div>

  <TSDialog
    label="Project Entity Details"
    v-model:visible="displayProjectEntity"
    icon="info-circle"
    size="w-[1000px]!"
    v-on:after-hide="displayProjectEntity = false"
  >
    <template #content>
      <AddProjectEntity @entity-selected="addSource"></AddProjectEntity>
    </template>
  </TSDialog>

  <TSDialog :label="`${splitType} Splits`" v-model:visible="displaySplits" icon="chart-pie" v-on:after-hide="recalculate" size="w-[900px]!">
    <template #content>
      <SourceOfFundsSplits
        v-model:project-settings="projectSettings"
        v-model:project-entities="projectEntities"
        :splitType="splitType"
        @closed="displaySplits = false"
      ></SourceOfFundsSplits>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import CustomDeleteIconCellRenderer from "@/components/cell-renderers/CustomDeleteIconCellRenderer.vue";
import DefaultPayeeRenderer from "@/components/cell-renderers/DefaultPayeeRenderer.vue";
import { computed, ref } from "vue";
import { usePropsStore } from "@/stores/props";
import { type ProjectEntity, createProjectEntity } from "@/types/ProjectEntity";
import type { ProjectInfo, ProjectSettings } from "@/types/Projects";
import type { Entity } from "@/types/Entity";
import {
  currencyFormatter,
  entityAlreadyExistsInProject,
  getCellEditableClass,
  getEntityDisplay,
  getTableData,
  numberParser,
} from "@/services/Utility";
import type { DrawItem } from "@/types/DrawItem";
import { recalculateSourceOfFunds } from "@/services/SourceOfFundsService";
import { getRoleDisplayText } from "@/enums/ProjectRole";
import CustomTagRenderer from "../cell-renderers/CustomTagRenderer.vue";
import { CustomTagType } from "@/enums/CustomTagType";
import type {
  CellClassParams,
  EditableCallbackParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  ValueFormatterParams,
  ValueParserParams,
} from "ag-grid-community";
import type { EntityRetainage, Retainage } from "@/types/Retainage";
import { createNewRetainage } from "@/services/BudgetBuilderService";
import { SourcesColumns } from "@/enums/SourceColumns";

interface Props {
  entities: Entity[];
  drawNumber?: number;
  editing?: boolean;
  drawLocked?: boolean;
  projectInfo: ProjectInfo;
  contacts: ProjectEntity[];
  condensed?: boolean;
  entityRetainageList?: EntityRetainage[];
}
const props = withDefaults(defineProps<Props>(), {
  drawNumber: 0,
  editing: true,
  drawLocked: false,
  condensed: false,
});

const buttonHeight = props.condensed ? "h-8" : "h-10";
const getRowHeight = (params: any): number => {
  if (props.condensed) return params.node.rowPinned ? 22 : 28;
  return params.node.rowPinned ? 45 : 45;
};

const entityType = ref<"Lender" | "Borrower">("Lender");

const projectSettings = defineModel<ProjectSettings>("projectSettings", { required: true });
const projectEntities = defineModel<ProjectEntity[]>("projectEntities", { required: true });
const drawItems = defineModel<DrawItem[]>("drawItems", { required: true });
const entityRetainageList = defineModel<EntityRetainage[]>("entityRetainageList", { required: false });

const borrowerSplitPossible = ref(projectEntities.value.filter((source) => source.type.toUpperCase().includes("BORROWER")).length > 1);
const lenderSplitPossible = ref(projectEntities.value.filter((source) => source.type.toUpperCase().includes("LENDER")).length > 1);

const gridApi = ref<GridApi>();

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const toggleEditing = (): void => {
  setTimeout(() => {
    gridApi.value?.setColumnsVisible(["delete"], props.editing);
    defaultColDefs.value.suppressMovable = !props.editing;
    resize();
    gridApi.value?.refreshCells({
      force: true,
      suppressFlash: true,
      columns: ["initialFunds", "fundsAdjustment", "fundsDisbursed", "targetTotalFunds", "closingCosts", "principalReduction", "defaultPayee"], // colId of the column you want to refresh
    });
  }, 10);
};

const rearrangeColumns = () => {
  const order: string[] = [];
  projectSettings.value.sourcesColumnOrder.forEach((col) => {
    const entry = Object.entries(SourcesColumns).find((entry) => entry[1].bit === col);
    if (entry == null) return;
    order.push(entry[0]);
  });
  colDefs.value = colDefs.value.sort((a, b) => order.indexOf(a.field ?? "") - order.indexOf(b.field ?? ""));
  gridApi.value?.setGridOption("columnDefs", colDefs.value);
};

const updateFundsDisbursed = (): void => {
  gridApi.value?.refreshCells({
    columns: ["fundsDisbursed"],
    force: true,
  });
};

const showParticipation = (type: string): void => {
  splitType.value = type;
  displaySplits.value = true;
};

const getSourceColumnOrder = (): number[] => {
  if (gridApi.value == null) return [];
  return gridApi.value.getColumnState().map((col: any) => {
    let fieldKey = col.colId as keyof typeof SourcesColumns;
    return SourcesColumns[fieldKey].bit;
  });
};

defineExpose({ toggleEditing, gridApi, rearrangeColumns, getSourceColumnOrder });
const emit = defineEmits<{
  deletedSource: [];
}>();

watch(
  () => projectSettings.value.enableRevolvingCredit,
  (enableRevolvingCredit: boolean) => gridApi.value?.setColumnsVisible(["targetTotalFunds"], enableRevolvingCredit)
);

watch(
  () => props.editing,
  (editing: boolean) => gridApi.value?.setColumnsVisible(["delete"], editing)
);

const displayProjectEntity = ref(false);
const displaySplits = ref(false);
const splitType = ref("");

const sourceFooter = ref([
  {
    type: "TOTAL",
    initialFunds: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.initialFunds, 0)),
    fundsAdjustment: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.fundsAdjustment, 0)),
    netFunds: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.netFunds, 0)),
    fundsDisbursed: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.fundsDisbursed, 0)),
    totalFundsDisbursed: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.totalFundsDisbursed, 0)),
    balance: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.balance, 0)),
    totalPrincipalReduction: computed<number>(() =>
      projectEntities.value.reduce((accumulator, current) => accumulator + current.totalPrincipalReduction, 0)
    ),
    totalFundsRetained: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.totalFundsRetained, 0)),
    totalFundsAdjustment: computed<number>(() =>
      projectEntities.value.reduce((accumulator, current) => accumulator + current.totalFundsAdjustment, 0)
    ),
    targetTotalFunds: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.targetTotalFunds, 0)),
    closingCosts: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.closingCosts, 0)),
    principalReduction: computed<number>(() => projectEntities.value.reduce((accumulator, current) => accumulator + current.principalReduction, 0)),
  },
]);

const recalculate = async (event: any): Promise<void> => {
  await recalculateSourceOfFunds(
    projectEntities.value,
    drawItems.value,
    props.entityRetainageList,
    projectSettings.value,
    props.drawNumber === 0
  ).then((recalcEntities: ProjectEntity[]) => (projectEntities.value = recalcEntities));
};

const rowClassRules = ref({
  "font-bold": (params: RowClassParams) => {
    return params.node.rowPinned != null;
  },
});

const defaultColDefs = ref({
  floatingFilter: false,
  cellDataType: "number",
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  suppressMovable: !props.editing,
});

const resize = (): void => {
  gridApi.value?.sizeColumnsToFit();
};

const cellIsEditable = (data: ProjectEntity): boolean => {
  return data.projectEntityId != null && props.editing;
};

//They should already be sorted, but just checking
const sortSources = (): void => {
  const borrowers: ProjectEntity[] = projectEntities.value
    .filter((pe: ProjectEntity) => pe.type.toUpperCase().includes("BORROWER"))
    .sort((a, b) => a.ordinal - b.ordinal);
  const lenders: ProjectEntity[] = projectEntities.value
    .filter((pe: ProjectEntity) => pe.type.toUpperCase().includes("LENDER"))
    .sort((a, b) => a.ordinal - b.ordinal);
  projectEntities.value = borrowers.concat(lenders);
};

const deleteSource = (source: ProjectEntity): void => {
  for (let i = 0; i < projectEntities.value.length; i++) {
    if (projectEntities.value[i].ordinal > source.ordinal) projectEntities.value[i].ordinal -= 1;
  }

  if (entityRetainageList.value) {
    const index = entityRetainageList.value.findIndex((er) => er.projectEntityId === source.projectEntityId);
    entityRetainageList.value.splice(index, 1);

    let entityNum = 1;
    let type = "L";
    entityRetainageList.value.forEach((er) => {
      if (type === "L" && er.projectEntityDisplay.startsWith("B")) {
        entityNum = 1;
        type = "B";
      }

      er.projectEntityDisplay = type + entityNum + ")" + er.projectEntityDisplay.split(")")[1];
      entityNum++;
    });

    projectSettings.value.enableLenderParticipation = projectSettings.value.enableLenderParticipation && lenderSplitPossible.value;
    projectSettings.value.enableEquityParticipation = projectSettings.value.enableEquityParticipation && borrowerSplitPossible.value;
  }

  gridApi.value?.applyTransaction({
    remove: [source],
  });
  const rowData = getTableData(gridApi.value);
  borrowerSplitPossible.value = rowData.filter((source: ProjectEntity) => source.type.toUpperCase().includes("BORROWER")).length > 1;
  if (!borrowerSplitPossible.value) projectSettings.value.enableEquityParticipation = false;
  lenderSplitPossible.value = rowData.filter((source: ProjectEntity) => source.type.toUpperCase().includes("LENDER")).length > 1;
  if (!lenderSplitPossible.value) projectSettings.value.enableLenderParticipation = false;
  projectEntities.value = rowData;
  success("Entity Deleted", "An entity was successfully deleted from the project");
  nextTick(() => emit("deletedSource"));
};

const setDefaultPayee = (id: string, checked: boolean): void => {
  gridApi.value?.forEachNode((row: any) => {
    if (checked) {
      row.setDataValue("defaultPayee", row.id === id);
    } else {
      row.setDataValue("defaultPayee", false);
    }
  });
};

const cellClass = (row: any): string => {
  if (row.colDef.editable && row.colDef.editable(row)) return getCellEditableClass();
  else return "";
};

const isDeletable = (data: ProjectEntity): boolean => {
  if (data.type.toUpperCase().includes("PRIMARY") || data.projectEntityId == null) return false;
  return (
    data.initialFunds === 0 &&
    data.totalFundsAdjustment === 0 &&
    data.netFunds === 0 &&
    data.totalFundsDisbursed === 0 &&
    data.totalPrincipalReduction === 0 &&
    data.balance === 0
  );
};

const colDefs = ref([
  {
    field: "delete",
    headerName: "",
    cellClass: "text-center flex items-center justify-center",
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: (data: ProjectEntity) => `Are you sure you want to delete ${getEntityDisplay(data.entity)} from the project?`,
      deleteConfirmHeader: (data: ProjectEntity) => `Delete ${data.type}?`,
      callback: deleteSource,
      checkDeletable: (data: ProjectEntity) => isDeletable(data),
      size: props.condensed ? "small" : "",
    },
    minWidth: 50,
    width: 50,
    initialHide: !props.editing,
    pinned: true,
  },
  {
    field: "type",
    headerName: "Type",
    cellDataType: "text",
    headerClass: "grid-align-center text-center",
    cellClass: "text-center",
    valueFormatter: (params: ValueFormatterParams) => {
      const value = getRoleDisplayText(params.value);
      return value.length > 0 ? value : params.value;
    },
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.SOURCE_TYPE,
    },
    minWidth: 175,
    width: 175,
    sortable: false,
    pinned: true,
  },
  {
    field: "contact",
    cellDataType: "text",
    headerName: "Contact",
    valueFormatter: (params: ValueFormatterParams) => getEntityDisplay(params.data.entity),
    comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any, isDescending: any) =>
      getEntityDisplay(nodeA.data.entity).localeCompare(getEntityDisplay(nodeB.data.entity)),
    headerClass: "grid-align-center text-center",
    minWidth: 150,
    width: 180,
    pinned: true,
  },
  {
    field: "companyName",
    cellDataType: "text",
    headerName: "Company Name",
    valueFormatter: (params: ValueFormatterParams) => (params.data.entity == null ? "" : params.data.entity.companyName),
    comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any, isDescending: any) =>
      nodeA.data.entity.companyName.localeCompare(nodeB.data.entity.companyName),
    headerClass: "grid-align-center text-center",
    minWidth: 150,
    width: 180,
    pinned: true,
  },
  {
    field: "initialFunds",
    headerName: "Initial Funds",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params),
    editable: (params: EditableCallbackParams) => cellIsEditable(params.data) && props.drawNumber === 0 && !props.drawLocked,
    cellClass: (params: CellClassParams) => cellClass(params),
    minWidth: 100,
  },
  {
    field: "fundsAdjustment",
    headerName: "Funds Adjustment",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    editable: (params: EditableCallbackParams) => cellIsEditable(params.data) && !props.drawLocked,
    cellClass: (params: CellClassParams) => cellClass(params),
    minWidth: 100,
  },
  {
    field: "netFunds",
    headerName: "Net Funds",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    minWidth: 100,
  },
  {
    field: "fundsDisbursed",
    headerName: "Funds Disbursed",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    editable: (params: EditableCallbackParams) => {
      const isBorrower = params.data.type.toUpperCase().includes("BORROWER");
      let autocalculate = projectSettings.value.autoDisburseFunds;
      if (!autocalculate) {
        if (isBorrower) {
          autocalculate = projectSettings.value.enableEquityParticipation;
        } else {
          autocalculate = projectSettings.value.enableLenderParticipation;
        }
      }

      return cellIsEditable(params.data) && !autocalculate && !props.drawLocked;
    },
    cellClass: (params: CellClassParams) => cellClass(params),
    minWidth: 100,
  },
  {
    field: "totalFundsDisbursed",
    headerName: "Total Funds Disbursed",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    minWidth: 100,
  },
  {
    field: "balance",
    headerName: "Funds Avail",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    minWidth: 100,
  },
  {
    field: "totalPrincipalReduction",
    headerName: "Total Principal Reduction",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    minWidth: 100,
  },
  {
    field: "totalFundsRetained",
    headerName: "Retain Balance",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    minWidth: 100,
  },
  {
    field: "totalFundsAdjustment",
    headerName: "Total Funds Adj",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    minWidth: 100,
  },
  {
    field: "targetTotalFunds",
    headerName: "Target Total Funds",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params),
    editable: (params: EditableCallbackParams) => cellIsEditable(params.data) && !props.drawLocked,
    cellClass: (params: CellClassParams) => cellClass(params),
    minWidth: 100,
    initialHide: !projectSettings.value.enableRevolvingCredit,
  },
  {
    field: "closingCosts",
    headerName: "Closing Costs",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params),
    editable: (params: EditableCallbackParams) => cellIsEditable(params.data) && props.drawNumber === 0 && !props.drawLocked,
    cellClass: (params: CellClassParams) => cellClass(params),
    minWidth: 100,
  },
  {
    field: "principalReduction",
    headerName: "Principal Reduction",
    headerClass: "grid-align-center text-center",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    editable: (params: EditableCallbackParams) => cellIsEditable(params.data) && !props.drawLocked,
    cellClass: (params: CellClassParams) => cellClass(params),
    minWidth: 100,
  },
  {
    field: "defaultPayee",
    cellDataType: "boolean",
    headerName: "Default Payee $",
    headerClass: "grid-align-center text-center",
    cellRenderer: DefaultPayeeRenderer,
    cellRendererParams: () => {
      return {
        setDefaultPayee: setDefaultPayee,
        disabled: !props.editing || props.drawLocked,
      };
    },
    cellClass: "text-center",
    minWidth: 75,
    width: 75,
  },
]);

const addSourceClick = (type: "Lender" | "Borrower"): void => {
  displayProjectEntity.value = true;
  entityType.value = type;
};

const addSource = (entity: Entity): void => {
  if (entityAlreadyExistsInProject(props.projectInfo, projectEntities.value, props.contacts, entity)) return;

  let insertLocation = projectEntities.value.length + 1;
  if (entityType.value == "Borrower") {
    let primaryLender = projectEntities.value.find((source) => source.type.toUpperCase() == "PRIMARY LENDER");
    insertLocation = primaryLender ? projectEntities.value.indexOf(primaryLender) : 1;
    borrowerSplitPossible.value = true;
  } else {
    lenderSplitPossible.value = true;
  }
  const row = createProjectEntity(
    entityType.value.toUpperCase(),
    entity,
    projectEntities.value.length + 4,
    props.drawNumber,
    props.projectInfo.projectId == null ? "" : props.projectInfo.projectId
  );
  gridApi.value?.applyTransaction({
    add: [row],
    addIndex: insertLocation,
  });
  projectEntities.value = getTableData(gridApi.value);
  displayProjectEntity.value = false;

  if (entityRetainageList.value) {
    const type = entityType.value.toUpperCase() === "LENDER" ? "L" : "B";
    const amt = projectEntities.value.filter((pe) => pe.type.toUpperCase().includes(type)).length + 1;
    const er: EntityRetainage = {
      projectEntityId: row.projectEntityId ?? "",
      projectEntityDisplay: type + amt + ") " + getEntityDisplay(entity),
      retainageList: [],
      deletedRetainageList: [],
    };
    const retainageList: Retainage[] = [];
    drawItems.value.forEach((item) => retainageList.push(createNewRetainage(item, er.projectEntityId)));
    er.retainageList = retainageList;
    entityRetainageList.value.push(er);
    entityRetainageList.value.sort((a, b) => a.projectEntityDisplay.localeCompare(b.projectEntityDisplay));
  }
};

onBeforeMount(async (): Promise<void> => {
  sortSources();
  rearrangeColumns();
});
</script>
